import {Chip, Grid} from "@mui/material";
import {
	Checkbox,
	Dialog,
	DialogAlignment,
	DialogTitle,
	IconNames,
	layout,
	MaterialIcon,
	PushButton,
	typography,
} from "@variocube/cube-ui";
import React, {Fragment, useEffect, useState} from "react";
import {stylesheet} from "typestyle";
import {useLocalization} from "../i18n";
import {DeliveryState, OccupancyType} from "./types";

export interface DeliveryFilter {
	states?: DeliveryState[];
	tags?: string[];
	occupancyTypes?: OccupancyType[];
}

export interface DeliveryFilterProps {
	tags: string[];
	filter?: DeliveryFilter;
	onApplyFilter: (filter: DeliveryFilter) => void;
}

export function DeliveryFilter(props: Readonly<DeliveryFilterProps>) {
	const {t} = useLocalization();
	const [filterDialogOpen, setFilterDialogOpen] = useState(false);
	const handleFilter = () => {
		// On the right end of the filter bar add a filter button, if that is clicked offer a dedicated view where the user can check and uncheck states and tags above that provide shortcuts: To be stored (states: Created, Reserved), Overdue (state: Stored, tag: Overdue), To be picked up: (this is special because we need the same as overdue plus also all the outbound deliveries), To be checked: (alle picked up deliveries, state: PickedUp)
		setFilterDialogOpen(true);
	};

	const filterableStates = [
		DeliveryState.Draft,
		DeliveryState.Created,
		DeliveryState.Reserved,
		DeliveryState.Stored,
		DeliveryState.PickedUp,
		DeliveryState.Cancelled,
		DeliveryState.Returned,
		DeliveryState.ManualHandover,
		DeliveryState.Shipped,
	];
	const [selectedStates, setSelectedStates] = useState<DeliveryState[]>([]);
	const [selectedTags, setSelectedTags] = useState<string[]>([]);
	const [selectedOccupancyTypes, setSelectedOccupancyTypes] = useState<OccupancyType[]>([]);

	const [updateFilter, setUpdateFilter] = useState(false);

	const noFilterSet = Boolean(
		selectedStates.length === 0 && selectedTags.length === 0 && selectedOccupancyTypes.length === 0,
	);

	useEffect(() => {
		applyFilter();
	}, [updateFilter]);

	const prefilter = (namedFilter: "To be stored" | "Overdue" | "To be picked up" | "To be checked") => {
		setSelectedTags([]);
		setSelectedStates([]);
		setSelectedOccupancyTypes([]);
		switch (namedFilter) {
			case "To be stored":
				setSelectedStates([DeliveryState.Created, DeliveryState.Reserved]);
				break;
			case "Overdue":
				setSelectedStates([DeliveryState.Stored]);
				setSelectedTags(["overdue"]);
				break;
			case "To be picked up":
				setSelectedStates([DeliveryState.Stored, DeliveryState.Returned, DeliveryState.Shipped]);
				setSelectedOccupancyTypes([OccupancyType.Outbound]);
				break;
			case "To be checked":
				setSelectedStates([DeliveryState.PickedUp]);
				break;
		}
	};

	const handleStateSelected = (state: DeliveryState, selected: boolean) => {
		if (selected) {
			const newSelectedStates = selectedStates.filter(s => s !== state);
			newSelectedStates.push(state);
			setSelectedStates(newSelectedStates);
		} else {
			const selectedStatesReduced = selectedStates.filter(s => s !== state);
			setSelectedStates(selectedStatesReduced);
		}
	};

	const handleTagSelected = (tag: string, selected: boolean) => {
		if (selected) {
			const newSelectedTags = selectedTags.filter(s => s !== tag);
			newSelectedTags.push(tag);
			setSelectedTags(newSelectedTags);
		} else {
			const selectedTagsReduced = selectedTags.filter(s => s !== tag);
			setSelectedTags(selectedTagsReduced);
		}
	};

	const cancelFilter = () => {
		setFilterDialogOpen(false);
		const oldFilter = {...props.filter};
		props.onApplyFilter(oldFilter);
		setSelectedStates(oldFilter.states as any);
		setSelectedTags(oldFilter.tags as any);
		setSelectedOccupancyTypes(oldFilter.occupancyTypes as any);
	};

	const applyFilter = () => {
		setFilterDialogOpen(false);
		props.onApplyFilter({
			states: selectedStates,
			tags: selectedTags,
			occupancyTypes: selectedOccupancyTypes,
		});
	};

	const removeStateFilter = (state: DeliveryState) => {
		handleStateSelected(state, false);
		setUpdateFilter(!updateFilter);
	};

	const removeTagFilter = (tag: string) => {
		setSelectedTags(selectedTags.filter(s => s !== tag));
		setUpdateFilter(!updateFilter);
	};

	const removeOccupancyTypeFilter = (type: OccupancyType) => {
		setSelectedOccupancyTypes(selectedOccupancyTypes.filter(s => s !== type));
		setUpdateFilter(!updateFilter);
	};

	const computeDisplayForEnum = (enumValue: string) => {
		const display = t(enumValue as any);
		if (display) {
			return display;
		}
		return enumValue;
	};

	return (
		<Fragment>
			<Grid container spacing={2} className={styles.filterPanel}>
				<Grid xs={11} item>
					{noFilterSet && (
						<Chip
							variant="outlined"
							className={styles.chipss}
							style={{padding: "32px", marginLeft: "16px"}}
							label={t("Filter.NoFilter")}
							onClick={() => handleFilter()}
						/>
					)}
					{selectedStates && (
						<Fragment>
							{selectedStates?.map(s => (
								<Chip
									className={styles.chipss}
									style={{padding: "32px", marginLeft: "16px"}}
									label={t(s)}
									onClick={() => removeStateFilter(s)}
									onDelete={() => removeStateFilter(s)}
								/>
							))}
						</Fragment>
					)}
					{selectedTags && (
						<Fragment>
							{selectedTags?.map(s => (
								<Chip
									className={styles.chipss}
									style={{padding: "32px", marginLeft: "16px"}}
									label={s == "overdue" ? t("Filter.Overdue") : s}
									onClick={() => removeTagFilter(s)}
									onDelete={() => removeTagFilter(s)}
								/>
							))}
						</Fragment>
					)}
					{selectedOccupancyTypes && (
						<Fragment>
							{selectedOccupancyTypes?.map(s => (
								<Chip
									className={styles.chipss}
									style={{padding: "32px", marginLeft: "16px"}}
									label={s}
									onClick={() => removeOccupancyTypeFilter(s)}
									onDelete={() => removeOccupancyTypeFilter(s)}
								/>
							))}
						</Fragment>
					)}
				</Grid>
				<Grid xs={1} item>
					<PushButton onClick={handleFilter}>
						<MaterialIcon icon={IconNames.filterList} className="filter" />
					</PushButton>
				</Grid>
			</Grid>

			<Dialog open={filterDialogOpen} align={DialogAlignment.FullScreen}>
				<div style={{padding: "1em"}}>
					<DialogTitle>{t("Filter.Title")}</DialogTitle>

					<div
						style={{
							display: "flex",
							flexDirection: "row",
							gap: "1em",
						}}
					>
						<PushButton onClick={() => prefilter("To be stored")}>{t("Filter.To Be Stored")}</PushButton>
						<PushButton onClick={() => prefilter("Overdue")}>{t("Filter.Overdue")}</PushButton>
						<PushButton onClick={() => prefilter("To be picked up")}>
							{t("Filter.To be picked up")}
						</PushButton>
						<PushButton onClick={() => prefilter("To be checked")}>{t("Filter.To be checked")}</PushButton>
					</div>

					<Grid container spacing={2} style={{padding: "32px"}}>
						<Grid xs={6} item>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<span className={typography.bigger}>
										{t("Filter.ByDeliveryState")}
									</span>
								</Grid>
								<Grid item xs={12}>
									{selectedStates
										&& filterableStates.map(s => (
											<Checkbox
												key={s}
												checked={selectedStates.indexOf(s) > -1}
												onChange={change => handleStateSelected(s, change)}
											>
												{computeDisplayForEnum(s)}
											</Checkbox>
										))}
								</Grid>
							</Grid>
						</Grid>
						<Grid xs={6} item>
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<span className={typography.bigger}>
										{t("Filter.ByDeliveryTag")}
									</span>
								</Grid>
								<Grid item xs={12}>
									{selectedTags
										&& props.tags.map(s => (
											<Checkbox
												key={s}
												checked={selectedTags.indexOf(s) > -1}
												onChange={change => handleTagSelected(s, change)}
											>
												{computeDisplayForEnum(s)}
											</Checkbox>
										))}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<div style={{margin: "1em"}} className={layout.alignEnd}>
						<PushButton primary onClick={applyFilter} className={styles.action}>
							{t("Filter.Apply")}
						</PushButton>
						<PushButton onClick={cancelFilter} className={styles.action}>
							{t("Filter.Cancel")}
						</PushButton>
					</div>
				</div>
			</Dialog>
		</Fragment>
	);
}

const styles = stylesheet({
	chipss: {
		padding: "32px",
		fontSize: "1.75rem",
		marginLeft: "16px",
	},
	action: {
		marginLeft: "1em",
		marginBottom: "1em",
		cursor: "pointer",
		position: "relative",
		float: "right",
	},
	filterPanel: {
		paddingRight: "32px",
	},
});
